import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

/**
 * Loyalty Cards page — 3-tier catalogue.
 *
 * Classic 6+6 was retired 2026-08-06; the page now only shows the 3
 * active tiers (Strip Starter €10 / Double Duo €15 / VIP Six €30). Tier
 * data is served from GET /api/loyalty/tiers so the catalogue can be
 * evolved server-side without a frontend redeploy.
 */
export default function LoyaltyCards() {
  const { t } = useLang();
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    api
      .get("/loyalty/tiers")
      .then(({ data }) => setTiers(data?.tiers || []))
      .catch(() => setTiers([]));
  }, []);

  return (
    <div
      data-testid="loyalty-cards-page"
      className="max-w-[1200px] mx-auto px-4 md:px-12 pt-16 md:pt-24 pb-24 md:pb-32"
    >
      <div className="text-center">
        <h1 className="sr-only">Pick Your Card</h1>
        <img
          src="/banners/pick-your-card.png"
          alt="Pick Your Card"
          data-testid="tickets-hero-banner"
          loading="eager"
          decoding="async"
          className="block w-full max-w-[900px] mx-auto h-auto"
        />
        <p className="text-white/60 mt-4 max-w-2xl mx-auto text-sm md:text-base">
          Pick a card. Skip the queue. Redeem drink-by-drink at the bar.
        </p>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mt-12"
        data-testid="loyalty-tier-grid"
      >
        {tiers.map((tier) => (
          <div
            key={tier.id}
            data-testid={`loyalty-tier-${tier.id}`}
            className="flex flex-col rounded-3xl overflow-hidden relative bg-black/60"
            style={{
              boxShadow:
                "0 0 25px rgba(255,27,141,0.35), 0 12px 40px rgba(0,0,0,0.6)",
              border: "1.5px solid rgba(255,27,141,0.45)",
            }}
          >
            <div
              className="relative aspect-[3/2] overflow-hidden"
              style={{ background: "#0a0007" }}
            >
              <img
                loading="lazy"
                decoding="async"
                src={tier.image}
                alt={`${tier.name} — ${tier.tagline}`}
                data-testid={`loyalty-tier-art-${tier.id}`}
                draggable={false}
                className="block w-full h-full object-cover select-none"
              />
              {tier.top_shelf ? (
                <span
                  className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.28em] font-bold text-black px-2 py-1 rounded-full"
                  style={{
                    background:
                      "linear-gradient(135deg,#FFE9A8 0%,#FFC94A 100%)",
                  }}
                >
                  Top Shelf
                </span>
              ) : null}
            </div>
            <div className="flex-1 flex flex-col p-5">
              <div className="font-heading text-2xl md:text-3xl uppercase leading-tight">
                {tier.name}
              </div>
              <div className="mt-1 text-xs uppercase tracking-widest text-white/50">
                {tier.tagline}
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-heading text-4xl text-neon">
                  €{tier.price_eur}
                </span>
                <span className="text-[10px] uppercase tracking-widest text-white/40">
                  paid in full
                </span>
              </div>
              <Link
                to={`/loyalty/buy?tier=${tier.id}`}
                data-testid={`loyalty-tier-buy-${tier.id}`}
                className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-bold text-black uppercase tracking-[0.22em] text-xs"
                style={{
                  background:
                    "linear-gradient(135deg,#FF1B8D 0%,#FF6BC6 50%,#FF1B8D 100%)",
                  boxShadow:
                    "0 0 20px rgba(255,27,141,0.55), inset 0 1px 0 rgba(255,255,255,0.4)",
                }}
              >
                <Sparkles size={14} /> Buy €{tier.price_eur}
              </Link>
            </div>
          </div>
        ))}
      </div>

      <p className="mt-10 text-center text-[10px] uppercase tracking-[0.32em] text-white/40">
        {t("loyalty.home.disclaimer")}
      </p>
    </div>
  );
}
